import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell, { SettingsGroup } from '../components/layout/AppShell';
import DesignSystem from '../designSystem';
import { useAuth } from '../contexts/AuthContext';
import { getApiLanguage } from '../config/apiLanguage';
import {
  fetchSupportCategories,
  submitSupportMessage,
  SupportCategory,
  SupportMessageType,
} from '../services/api';
import { useT } from '../i18n/useT';

export default function ReportProblemPage() {
  const t = useT();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mode: SupportMessageType = params.get('mode') === 'request' ? 'request' : 'problem';

  const [categories, setCategories] = useState<SupportCategory[]>([]);
  const [category, setCategory] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetchSupportCategories(mode, user.token, getApiLanguage())
      .then((list) => {
        setCategories(list);
        if (list[0]) setCategory(list[0].id);
      })
      .catch((e) => setError(e instanceof Error ? e.message : t('supportLoadCategoriesFailed')))
      .finally(() => setLoading(false));
  }, [user?.token, mode]);

  const title = mode === 'request' ? t('reportRequestTitle') : t('reportProblemTitle');

  const send = async () => {
    if (!user?.token) return;
    setSending(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await submitSupportMessage(mode, category, body.trim(), user.token, getApiLanguage());
      setSuccess(res.message || res.title || t('sentDefault'));
      setBody('');
      setTimeout(() => navigate('/settings'), 1200);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('supportSendFailed'));
    } finally {
      setSending(false);
    }
  };

  return (
    <AppShell title={title} showBackButton maxWidth="sm" showCompanion={false}>
      <Typography sx={{ mb: 2, fontSize: 14, color: DesignSystem.colors.textSecondary }}>
        {mode === 'request' ? t('reportRequestSubtitle') : t('reportProblemSubtitle')}
      </Typography>
      {loading && <Typography color="text.secondary">{t('loading')}</Typography>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {!loading && (
        <SettingsGroup>
          <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="support-cat">{t('supportCategory')}</InputLabel>
              <Select
                labelId="support-cat"
                label={t('supportCategory')}
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              multiline
              minRows={5}
              fullWidth
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={mode === 'request' ? t('reportPlaceholderRequest') : t('reportPlaceholderProblem')}
            />
            <Button
              variant="contained"
              disabled={sending || !category || !body.trim()}
              onClick={() => void send()}
              sx={{ alignSelf: 'flex-start', textTransform: 'none', borderRadius: 999, px: 3, boxShadow: 'none' }}
            >
              {sending ? t('sending') : t('send')}
            </Button>
          </Box>
        </SettingsGroup>
      )}
    </AppShell>
  );
}
